// Components/AIChat/ListaConversasAIChat.jsx
import React, { useCallback, useEffect, useState, Fragment } from "react";
import { ListarConversasAIChat } from "../../Services/AI";
import AIChat from "./AIChat";
import { FormatDateTimeChat, TruncateText, NewGuid } from "../../Utils/globalFunctions";
import "./ListaConversasAIChat.css";

const DEFAULT_TAKE = 30;

const ListaConversasAIChat = ({ id }) => {
    const [conversas, setConversas] = useState([]);
    const [conversaSelecionada, setConversaSelecionada] = useState(null);

    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);
    const [modalAberto, setModalAberto] = useState(false);

    const carregarConversas = useCallback(async () => {
        try {
            setErro(null);
            setLoading(true);

            const { success, conversas } = await ListarConversasAIChat(0, DEFAULT_TAKE);

            if (success) {
                setConversas(conversas || []);
            } else {
                setErro("Não foi possível carregar as conversas.");
            }
        } catch (e) {
            console.error("Erro ao listar conversas AIChat:", e);
            setErro("Não foi possível carregar as conversas.");
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshConversasESelecionar = useCallback(async (conversationIdToSelect) => {
        try {
            setErro(null);
            setLoading(true);

            const { success, conversas } = await ListarConversasAIChat(0, DEFAULT_TAKE);

            if (!success) {
                setErro("Não foi possível carregar as conversas.");
                return;
            }

            const lista = conversas || [];
            setConversas(lista);

            if (conversationIdToSelect) {
                const encontrada = lista.find(x => x.conversationId === conversationIdToSelect);
                if (encontrada) setConversaSelecionada(encontrada);
            }
        } catch (e) {
            console.error("Erro ao listar conversas AIChat:", e);
            setErro("Não foi possível carregar as conversas.");
        } finally {
            setLoading(false);
        }
    }, []);

    const iniciarNovaConversa = () => {
        const conversationId = NewGuid();

        const conversaNova = {
            chatConversaId: null,
            conversationId,
            produtoChave: null,
            atualizadoEm: new Date().toISOString(),
            ultimaMensagemPreview: "",
            quantidadeInteracoes: 0,
            __nova: true
        };

        setConversaSelecionada(conversaNova);
    };

    // carrega quando abre o modal
    useEffect(() => {
        if (!modalAberto) return;
        carregarConversas();
    }, [modalAberto, carregarConversas]);

    const obterIniciais = (texto) => {
        if (!texto || typeof texto !== "string") return "AI";
        const partes = texto.trim().split(/\s+/);
        if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
        return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
    };

    const getTituloConversa = (c) => {
        if (c?.produtoChave) return `AI • ${c.produtoChave}`;
        if (c?.conversationId) return `AI • ${c.conversationId}`;
        return "AI • conversa";
    };

    const renderConversaItem = (c) => {
        const selecionada =
            (conversaSelecionada?.chatConversaId && conversaSelecionada.chatConversaId === c.chatConversaId) ||
            (!conversaSelecionada?.chatConversaId && conversaSelecionada?.conversationId === c.conversationId);

        const titulo = getTituloConversa(c);

        return (
            <button
                key={c.chatConversaId}
                type="button"
                className={`aichat-lista-item ${selecionada ? "selecionada" : ""}`}
                onClick={() => setConversaSelecionada(c)}
            >
                <div className="aichat-avatar">
                    <span>{obterIniciais(c?.produtoChave || "AI")}</span>
                </div>

                <div className="aichat-conteudo">
                    <div className="aichat-header">
                        <span className="aichat-nome">{titulo}</span>
                        <span className="aichat-hora">{FormatDateTimeChat(c?.atualizadoEm)}</span>
                    </div>

                    <div className="aichat-footer">
                        <span className="aichat-ultima-msg">
                            {TruncateText(c?.ultimaMensagemPreview || "Sem mensagens ainda", 70)}
                        </span>
                        <span className="aichat-chip">{c?.quantidadeInteracoes ?? 0}</span>
                    </div>
                </div>
            </button>
        );
    };

    return (
        <Fragment>
            <div
                className="aichat-launcher"
                role="button"
                data-toggle="modal"
                data-target={`#modalAIChat${id}`}
                onClick={() => {
                    setModalAberto(true);
                    setConversaSelecionada(null);
                }}
                title="Abrir AIChat"
            >
                <div className="aichat-launcher-circle">
                    <span className="aichat-launcher-text">Ôlivia</span>
                </div>
            </div>

            {/* Modal */}
            <div
                className="modal fade"
                id={`modalAIChat${id}`}
                tabIndex="-1"
                role="dialog"
                aria-labelledby={`modalAIChat${id}Label`}
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                    <div className="modal-content aichat-modalContent">
                        <div className="modal-header aichat-headerBar">
                            <h5 className="modal-title" id={`modalAIChat${id}Label`}>
                                Ôlivia
                            </h5>

                            <div className="aichat-header-actions">
                                <button
                                    type="button"
                                    className="close"
                                    data-dismiss="modal"
                                    aria-label="Close"
                                    onClick={() => {
                                        setModalAberto(false);
                                        setConversaSelecionada(null);
                                    }}
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>

                        <div className="modal-body aichat-modalBody">
                            <div className="aichat-container">
                                {/* Sidebar */}
                                <div className="aichat-sidebar">
                                    <div className="aichat-sidebar-header aichat-sidebar-header--row">
                                        <span>Conversas</span>

                                        <button
                                            type="button"
                                            className="aichat-btn-nova"
                                            onClick={iniciarNovaConversa}
                                            title="Iniciar nova conversa"
                                        >
                                            + Nova
                                        </button>
                                    </div>


                                    {loading && conversas.length === 0 && (
                                        <div className="aichat-info">Carregando conversas...</div>
                                    )}

                                    {erro && <div className="aichat-erro">{erro}</div>}

                                    {!loading && conversas.length === 0 && !erro && (
                                        <div className="aichat-info">Nenhuma conversa encontrada.</div>
                                    )}

                                    <div className="aichat-lista">{conversas.map(renderConversaItem)}</div>
                                </div>

                                <div className="aichat-chatArea">
                                    {conversaSelecionada ? (
                                        <AIChat
                                            titulo={getTituloConversa(conversaSelecionada)}
                                            chatConversaId={conversaSelecionada.chatConversaId}
                                            conversationId={conversaSelecionada.conversationId}
                                            onSentSuccess={() => refreshConversasESelecionar(conversaSelecionada.conversationId)}
                                        />
                                    ) : (
                                        <div className="aichat-placeholder">
                                            <h4>Selecione uma conversa</h4>
                                            <p>Escolha uma conversa ao lado para começar.</p>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ListaConversasAIChat;
